/*
 *  This file newrelic-router-tracker.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { Injectable } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationError, NavigationCancel, Event } from '@angular/router';
import { NewRelicHandlerService } from './newrelic-handler.service';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NewRelicRouterTrackerService {

  private navigationStartTime = 0;
  private currentUrl = '';
  private previousUrl = '';

  constructor(
    private readonly router: Router,
    private readonly _newRelic: NewRelicHandlerService
  ) {
    this.startTracking();
  }

  /**
   * Entry point to begin router tracking.
   * Called automatically on service construction so tracking starts at app initialization.
   * If New Relic is already initialized, tracking starts immediately.
   * Otherwise falls back to exponential-backoff polling until the agent is ready.
   */
  startTracking(): void {
    if (this._newRelic.isEnabled) {
      this.initializeTracking();
    } else {
      this.waitForNewRelic();
    }
  }

  /**
   * Polls for New Relic readiness using exponential backoff (100 ms → 3 s cap).
   * Gives up silently after maxAttempts to avoid hanging the app.
   * @param attempt - Current retry count (starts at 1).
   * @param maxAttempts - Maximum number of retries before giving up.
   */
  private waitForNewRelic(attempt = 1, maxAttempts = 10): void {
    const delay = Math.min(100 * Math.pow(2, attempt - 1), 3000);
    setTimeout(() => {
      if (this._newRelic.isEnabled) {
        this.initializeTracking();
      } else if (attempt < maxAttempts) {
        this.waitForNewRelic(attempt + 1, maxAttempts);
      } else {
        console.warn(`New Relic not ready after ${maxAttempts} attempts, router tracking disabled`);
      }
    }, delay);
  }

  /**
   * Registers subscriptions for all four Angular router event types:
   * NavigationStart, NavigationEnd, NavigationError, and NavigationCancel.
   * Each event fires a named page-action to New Relic with timing and context attributes.
   */
  private initializeTracking(): void {
    this.router.events.pipe(
      filter((event: Event): event is NavigationStart | NavigationEnd | NavigationError | NavigationCancel =>
        event instanceof NavigationStart ||
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        event instanceof NavigationCancel
      )
    ).subscribe((event: NavigationStart | NavigationEnd | NavigationError | NavigationCancel) => {
      if (event instanceof NavigationStart) {
        this.navigationStartTime = performance.now();
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;

        this._newRelic.addPageAction('RouteChangeStart', {
          url: event.url,
          previousUrl: this.previousUrl,
          navigationTrigger: event.navigationTrigger ?? 'imperative',
          restoredState: event.restoredState ? 'yes' : 'no'
        });
      } else if (event instanceof NavigationEnd) {
        const duration = performance.now() - this.navigationStartTime;

        this._newRelic.addPageAction('RouteChangeComplete', {
          url: event.urlAfterRedirects,
          previousUrl: this.previousUrl,
          duration: Math.round(duration),
          status: 'success'
        });

        this._newRelic.setPageViewName(this.extractPageName(event.urlAfterRedirects));
        this._newRelic.trackMetric('RouteChangeDuration', duration, 'ms');
      } else if (event instanceof NavigationError) {
        const duration = performance.now() - this.navigationStartTime;

        this._newRelic.addPageAction('RouteChangeError', {
          url: event.url,
          previousUrl: this.previousUrl,
          duration: Math.round(duration),
          status: 'error',
          errorMessage: event.error?.message ?? 'Unknown navigation error'
        });

        this._newRelic.noticeError(
          new Error(`Navigation Error: ${event.error?.message ?? 'Unknown error'}`),
          { errorType: 'NavigationError', url: event.url, previousUrl: this.previousUrl }
        );
      } else {
        const duration = performance.now() - this.navigationStartTime;

        this._newRelic.addPageAction('RouteChangeCancel', {
          url: event.url,
          previousUrl: this.previousUrl,
          duration: Math.round(duration),
          status: 'cancelled',
          reason: event.reason
        });
      }
    });
  }

  /**
   * Derives a clean, human-readable page name from a URL path.
   * Strips query params and fragments, capitalises each segment, and joins with dots.
   * Example: '/app/payroll/process-salary?tab=1' → 'App.Payroll.Process-salary'
   * @param url - Raw URL string from NavigationEnd.urlAfterRedirects.
   */
  private extractPageName(url: string): string {
    let cleanUrl = url.split('?')[0].split('#')[0];
    if (cleanUrl.startsWith('/')) {
      cleanUrl = cleanUrl.substring(1);
    }
    if (!cleanUrl) {
      return 'Home';
    }
    return cleanUrl
      .split('/')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('.');
  }

  /**
   * Manually records a named route event to New Relic outside of the Angular router lifecycle.
   * Useful for virtual page views, tab switches, or modal flows that don't trigger a real navigation.
   * @param routeName - Logical name for the route or view (e.g. 'PayslipModal', 'AttendanceTab').
   * @param metadata - Optional key-value pairs to attach to the event.
   */
  trackRouteManually(routeName: string, metadata?: Record<string, string | number | boolean>): void {
    this._newRelic.addPageAction('ManualRouteTrack', {
      routeName,
      url: window.location.href,
      ...metadata
    });
  }
}
